import React, { useState, useEffect } from "react"
import { useMutation, useQuery } from "urql"

import { useToast } from "../../contexts/ToastContext"
import { Button } from "../shared/Buttons"
import { AnimatedModal } from "../shared/Modal"
import Typography from "../shared/Typography"
import VideoUploadPage from "../VideoUploadPage"

const CHECK_VIDEO_REVIEW_AVAILABILITY_QUERY = `
  query CheckVideoReviewAvailability($reviewId: ID!) {
    review(token: $reviewId) {
      hasCompletedVideoReview
      videoReviews {
        status
      }
    }
  }
`

const CREATE_VIDEO_REVIEW_MUTATION = `
  mutation CreateVideoReviewForReview($reviewId: ID!) {
    createVideoReviewForReview(reviewId: $reviewId) {
      result
      uploadHash
      uploadUrl
      videoReview
      errors
    }
  }
`

const COMPLETE_TEXT_REVIEW_MUTATION = `
  mutation ReviewAddTextReview($reviewId: ID!, $text: String!) {
    reviewAddTextReview(reviewId: $reviewId, text: $text) {
      result
      errors
    }
  }
`

const VideoReviewModal = ({ review, isOpen, onClose }) => {
  const { showToast } = useToast()
  const [{ data: availabilityData, fetching: checkingAvailability }] = useQuery({
    query: CHECK_VIDEO_REVIEW_AVAILABILITY_QUERY,
    variables: { reviewId: review.token },
    pause: !isOpen // Only query when modal is open
  })
  const [, createVideoReview] = useMutation(CREATE_VIDEO_REVIEW_MUTATION)
  const [, completeTextReview] = useMutation(COMPLETE_TEXT_REVIEW_MUTATION)
  const [videoReviewData, setVideoReviewData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const hasCompletedVideoReview = availabilityData?.review?.hasCompletedVideoReview

  const handleCreateVideoReview = async () => {
    setIsLoading(true)
    try {
      const response = await createVideoReview({ reviewId: review.token })
      if (response.data.createVideoReviewForReview.result === "success") {
        // Parse the video review JSON data
        const videoReview = JSON.parse(response.data.createVideoReviewForReview.videoReview)

        // Prepare video review data for the upload component
        setVideoReviewData({
          ...videoReview,
          uploadHash: response.data.createVideoReviewForReview.uploadHash,
          canUpload: videoReview["can_upload?"]
        })
      } else {
        showToast({
          content: `Error creating video review: ${response.data.createVideoReviewForReview.errors.join(" ")}`,
          type: "error"
        })
      }
    } catch (error) {
      showToast({
        content: `Error creating video review: ${error.message}`,
        type: "error"
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleVideoUploadComplete = async () => {
    try {
      // Automatically complete the text review with a placeholder indicating video was submitted
      const textResponse = await completeTextReview({
        reviewId: review.token,
        text: ""
      })

      if (textResponse.data.reviewAddTextReview.result === "success") {
        showToast({ content: "Video review submitted successfully!", type: "success" })
        // Navigate to the next step (book_next)
        window.location.href = `/i/r/book_next/${review.token}`
      } else {
        showToast({
          content: `Error completing review: ${textResponse.data.reviewAddTextReview.errors.join(" ")}`,
          type: "error"
        })
        onClose()
      }
    } catch (error) {
      showToast({
        content: `Error completing review: ${error.message}`,
        type: "error"
      })
      onClose()
    }
  }

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setVideoReviewData(null)
      setIsLoading(false)
    }
  }, [isOpen])

  // If we have video review data, show the upload component
  if (videoReviewData) {
    return (
      <AnimatedModal
        visible={true}
        hideModal={onClose}
        header=""
        showFooter={false}
        overflowVisible={true}
        dialogClassName="max-w-4xl">
        <div className="h-[90vh] w-full">
          <VideoUploadPage
            videoReview={videoReviewData}
            practice={review.practice}
            client={review.client}
            uploadHash={videoReviewData.uploadHash}
            onComplete={handleVideoUploadComplete}
          />
        </div>
      </AnimatedModal>
    )
  }

  return (
    <AnimatedModal visible={isOpen} hideModal={onClose} header="" showFooter={false}>
      <div className="p-6 text-center">
        <Typography variant="h2" as="p">
          📹
        </Typography>

        {checkingAvailability ? (
          <>
            <Typography variant="h3" className="mt-2">
              Checking availability...
            </Typography>
          </>
        ) : hasCompletedVideoReview ? (
          <>
            <Typography variant="h3" className="mt-2">
              Video Review Already Submitted
            </Typography>
            <Typography variant="subtitle" className="mx-auto mt-2 max-w-sm">
              You have already submitted a video review for{" "}
              {review.practitioner?.firstName || review.practitioner?.name}. Only one video review per appointment is
              allowed.
            </Typography>
            <div className="mt-6">
              <Button type="primary" size="large" onClick={onClose}>
                Close
              </Button>
            </div>
          </>
        ) : (
          <>
            <Typography variant="h3" className="mt-2">
              Create a Video Review
            </Typography>
            <Typography variant="subtitle" className="mx-auto mt-2 max-w-sm">
              Share your experience with {review.practitioner?.firstName || review.practitioner?.name} in a video
              review. This will create a video upload link for your review.
            </Typography>
            <div className="mt-6 flex justify-center gap-3">
              <Button type="secondary" size="large" onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button type="primary" size="large" onClick={handleCreateVideoReview} disabled={isLoading}>
                {isLoading ? "Creating..." : "Start Video Review"}
              </Button>
            </div>
          </>
        )}
      </div>
    </AnimatedModal>
  )
}

export default VideoReviewModal
